import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExternalLink, GitPullRequest, History, ListChecks, Timer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { displayValue, hasDisplayValue } from "@/lib/display";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PRMergeConfirmDialog from "@/components/tickets/PRMergeConfirmDialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

function InfoRow({ label, value }) {
  const shownValue = displayValue(value);
  if (!hasDisplayValue(shownValue)) return null;

  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border py-3 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 max-w-full break-all text-right text-sm font-medium md:max-w-[70%]">{shownValue}</span>
    </div>
  );
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function PRTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [deploymentAt, setDeploymentAt] = useState("");
  const [deploymentReference, setDeploymentReference] = useState("");
  const [deploymentReleaseName, setDeploymentReleaseName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pr/tickets/${id}`);
      setTicket(data);
      setLoadError("");
      setDeploymentAt(toDateTimeLocal(data.deployment_at));
      setDeploymentReference(data.deployment_reference || "");
      setDeploymentReleaseName(data.deployment_release_name || "");
      return true;
    } catch (error) {
      const message = formatApiError(error);
      setLoadError(message);
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const markMerged = async () => {
    setMerging(true);
    try {
      await api.post(`/tickets/${id}/pr/merge`, { merged: true });
      setMergeConfirmOpen(false);
      toast.success("PR marked as merged");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setMerging(false);
    }
  };

  const refreshDetails = async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const saveDeployment = async (event) => {
    event.preventDefault();
    if (!deploymentReference.trim() || !deploymentReleaseName.trim()) {
      toast.error("Deployment reference and release are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tickets/${id}/deployment`, {
        deployment_at: deploymentAt || undefined,
        deployment_reference: deploymentReference.trim(),
        deployment_release_name: deploymentReleaseName.trim(),
      });
      toast.success("Deployment details saved");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="space-y-6"><div className="rounded-xl border border-border bg-card p-8 text-sm text-muted-foreground">Loading PR details...</div></div>;
  }

  if (!ticket) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">{loadError || "PR details could not be loaded."}</p>
        <div className="mt-4 flex gap-2">
          <Button onClick={refreshDetails} disabled={refreshing}>{refreshing ? "Refreshing..." : "Try again"}</Button>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to PR dashboard</Button>
        </div>
      </div>
    );
  }

  const merged = ticket.pr_status === "merged" || Boolean(ticket.pr_merged_at);
  const activity = ticket.activity || [];
  const activityLabels = {
    "pr.raised": "PR raised",
    "pr.merged": "PR merged",
    "deployment.recorded": "Deployment recorded",
  };

  return (
    <div className="space-y-6" data-testid="pr-ticket-detail-page">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button type="button" onClick={() => navigate("/dashboard")} className="transition-colors hover:text-foreground">PR Dashboard</button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage>{ticket.ticket_number}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="glass rounded-xl border border-border p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <span>{ticket.ticket_number}</span>
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-sans ${merged ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>
                <GitPullRequest className="h-3.5 w-3.5" /> {merged ? "Merged" : "Awaiting merge"}
              </span>
            </div>
            <h1 className="mt-2 break-all font-display text-2xl font-bold tracking-tight md:text-3xl">{ticket.pr_reference}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {hasDisplayValue(ticket.pr_raised_by_name) && <span>Raised by {displayValue(ticket.pr_raised_by_name)}</span>}
                {ticket.pr_raised_at && <span>Raised {formatDateTime(ticket.pr_raised_at)}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {ticket.pr_url && <Button asChild variant="outline" size="sm"><a href={ticket.pr_url} target="_blank" rel="noreferrer">Open pull request <ExternalLink className="h-3.5 w-3.5" /></a></Button>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <Tabs defaultValue="details" className="min-w-0 space-y-4">
          <TabsList className="bg-secondary/60">
            <TabsTrigger value="details"><ListChecks className="mr-1 h-3.5 w-3.5" />Details</TabsTrigger>
            <TabsTrigger value="activity"><History className="mr-1 h-3.5 w-3.5" />Activity History ({activity.length})</TabsTrigger>
            <TabsTrigger value="deployment"><Timer className="mr-1 h-3.5 w-3.5" />Deployment</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 rounded-xl border border-border bg-card p-5">
            <section>
              <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pull request</div>
              <InfoRow label="PR reference" value={ticket.pr_reference} />
              <InfoRow label="PR status" value={merged ? "Merged" : "Awaiting merge"} />
              <InfoRow label="Raised by" value={ticket.pr_raised_by_name} />
              <InfoRow label="Raised at" value={formatDateTime(ticket.pr_raised_at)} />
              <InfoRow label="Merged at" value={merged ? formatDateTime(ticket.pr_merged_at) : null} />
              <InfoRow label="PR URL" value={ticket.pr_url ? <a href={ticket.pr_url} target="_blank" rel="noreferrer" className="break-all text-primary hover:underline">{ticket.pr_url}</a> : null} />
            </section>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <section className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">PR status</div>
                <div className="font-semibold">{merged ? "Merged" : "Awaiting merge"}</div>
                {merged && ticket.pr_merged_at && <div className="mt-1 text-xs text-muted-foreground">Merged {formatDateTime(ticket.pr_merged_at)}</div>}
              </section>
              <section className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Deployment status</div>
                <div className="font-semibold">{displayValue(ticket.deployment_reference)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{ticket.deployment_at ? formatDateTime(ticket.deployment_at) : null}</div>
              </section>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="rounded-xl border border-border bg-card p-5">
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">PR activity</div>
            {activity.length === 0 ? (
              <div className="py-4 text-sm text-muted-foreground">No PR activity has been recorded.</div>
            ) : (
              <ol className="space-y-4">
              {activity.map((event, index) => (
                <li key={`${event.event_type}-${index}`} className="flex gap-3 border-b border-border pb-4 last:border-0 last:pb-0">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{event.message || activityLabels[event.event_type] || "PR activity"}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{event.actor_name || "Unknown"} - {formatDateTime(event.created_at)}</div>
                  </div>
                </li>
              ))}
              </ol>
            )}
          </TabsContent>

          <TabsContent value="deployment" className="space-y-4">
            <section className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Deployment details</div>
              <InfoRow label="Deployment reference" value={ticket.deployment_reference} />
              <InfoRow label="Release" value={ticket.deployment_release_name} />
              <InfoRow label="Deployed at" value={formatDateTime(ticket.deployment_at)} />
            </section>
            <form onSubmit={saveDeployment} className="rounded-xl border border-border bg-card p-5 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Record deployment</div>
              <div><Label htmlFor="pr-deployment-reference">Deployment reference</Label><Input id="pr-deployment-reference" value={deploymentReference} onChange={(event) => setDeploymentReference(event.target.value)} placeholder="Release or deployment ID" required /></div>
              <div><Label htmlFor="pr-deployment-release">Release name</Label><Input id="pr-deployment-release" value={deploymentReleaseName} onChange={(event) => setDeploymentReleaseName(event.target.value)} placeholder="Version or release name" required /></div>
              <div><Label htmlFor="pr-deployment-at">Deployment date</Label><Input id="pr-deployment-at" type="datetime-local" value={deploymentAt} onChange={(event) => setDeploymentAt(event.target.value)} /></div>
              <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save deployment"}</Button>
            </form>
          </TabsContent>
        </Tabs>

        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Actions</div>
            {merged ? (
              <div className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">PR merged {formatDateTime(ticket.pr_merged_at)}.</div>
            ) : (
              <Button className="w-full" onClick={() => setMergeConfirmOpen(true)} disabled={merging}>
                Mark PR as merged
              </Button>
            )}
            <Button variant="outline" className="mt-2 w-full" onClick={refreshDetails} disabled={refreshing}>
              <GitPullRequest className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh PR details"}
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">PR overview</div>
            <div className="space-y-2 text-xs">
              <InfoRow label="Ticket" value={ticket.ticket_number} />
              <InfoRow label="Raised by" value={ticket.pr_raised_by_name} />
              <InfoRow label="PR status" value={merged ? "Merged" : "Awaiting merge"} />
              <InfoRow label="Deployment" value={ticket.deployment_reference} />
            </div>
          </div>
        </aside>
      </div>
      <PRMergeConfirmDialog
        open={mergeConfirmOpen}
        prReference={ticket.pr_reference}
        loading={merging}
        onCancel={() => setMergeConfirmOpen(false)}
        onConfirm={markMerged}
      />
    </div>
  );
}
